import type { ComponentPropsWithoutRef, JSX } from 'react';

import { useBlockSelector, useEditableText } from '@site-builder/block-helpers';
import { type HTMLElements, TextEditor } from '@site-builder/block-utils';

export const makeIntl = <
  Block extends { translations: Record<string, `L:${string}`> },
>() => {
  const useIntl = (
    id: keyof Block['translations'],
    values?: {
      [key: string]: string;
    },
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ref = useBlockSelector((b) => (b as any).translations[id]);

    return useEditableText(ref, values);
  };

  type FormattedMessageDisplayProps = {
    id: keyof Block['translations'];
    values?: Record<string, string>;
  };

  const FormattedMessageDisplay = ({
    id,
    values,
  }: FormattedMessageDisplayProps) => {
    const text = useIntl(id, values);

    return <>{text}</>;
  };

  type FormattedMessageProps = {
    id: keyof Block['translations'];
    editable?: boolean;
    values?: Record<string, string | number>;
  };

  function FormattedMessage<T extends keyof HTMLElements>(
    props: FormattedMessageProps &
      Omit<ComponentPropsWithoutRef<typeof TextEditor<T>>, 'id'>,
  ): JSX.Element;
  function FormattedMessage(
    props: FormattedMessageProps &
      Omit<ComponentPropsWithoutRef<typeof TextEditor>, 'id'>,
  ): JSX.Element;
  function FormattedMessage({
    id,
    editable = true,
    ...props
  }: FormattedMessageProps) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ref = useBlockSelector((b) => (b as any).translations[id]);

    return (
      <TextEditor
        id={ref}
        style={!editable ? { pointerEvents: 'none' } : undefined}
        {...props}
      />
    );
  }

  return {
    FormattedMessage,
    FormattedMessageDisplay,
    useIntl,
  };
};
